import cookieStorage from './cookie_storage';
import { COOKIE_KEYS } from '../constants';

/**
 * Persist user session data after a successful login.
 * Shared between agent (sign_in.js) and client (client_sign_in.js) flows to
 * eliminate the duplicated saveUserDataToLocalStorage functions.
 *
 * @param {object} user             - User object from login response
 * @param {string} token            - Access token
 * @param {string} refreshToken     - Refresh token
 * @param {object} opts
 * @param {Function} opts.setRolePermissions - From usePermissions() context
 */
export function saveUserData(user, token, refreshToken, { setRolePermissions }) {
  // Core auth
  cookieStorage.setItem(COOKIE_KEYS.TOKEN, token);
  cookieStorage.setItem(COOKIE_KEYS.REFRESH_TOKEN, refreshToken);
  cookieStorage.setItem(COOKIE_KEYS.MOBILE_VERIFIED, String(user?.mobileVerified));
  cookieStorage.setItem(COOKIE_KEYS.USER_ID, user?._id);
  cookieStorage.setItem(COOKIE_KEYS.USER_TYPE, user?.userType);
  cookieStorage.setItem(COOKIE_KEYS.USER_NAME, user?.fullname || '');

  if (!user?.mobileVerified) {
    cookieStorage.setItem(COOKIE_KEYS.MOBILE, user?.mobile || '');
  }

  if (user?.roleID?.permissions) {
    setRolePermissions(user?.roleID?.permissions);
  }

  // Preferences
  if (user?.preferences?.sessionTimeout) {
    cookieStorage.setItem(COOKIE_KEYS.SESSION_TIMEOUT, JSON.stringify(user?.preferences?.sessionTimeout));
  }
  if (user?.preferences?.lock?.enabled) {
    cookieStorage.setItem(COOKIE_KEYS.LOCK_ENABLED, String(user?.preferences?.lock?.enabled));
  }

  // Role fields (agents / employees)
  cookieStorage.setItem(COOKIE_KEYS.ROLE_ID, user?.roleID?._id || '');
  cookieStorage.setItem(COOKIE_KEYS.ROLE_NAME, user?.roleID?.name || '');
  cookieStorage.setItem(COOKIE_KEYS.ROLE_LEVEL, user?.roleID?.level || '');
  cookieStorage.setItem(COOKIE_KEYS.ROLE_TYPE, user?.roleID?.type || '');
  cookieStorage.setItem(COOKIE_KEYS.IS_LAST_LEVEL_IN_CLIENT_LEVEL, String(user?.isLastLevelInClientLevel || false));
  cookieStorage.setItem(COOKIE_KEYS.IS_LAST_LEVEL_IN_ORDER_LEVEL, String(user?.isLastLevelInOrderLevel || false));

  // Client-specific fields
  cookieStorage.setItem(COOKIE_KEYS.ASSIGNED_ACCOUNT_MANAGER_ID, user?.assignTo?.accountManager?.accountManagerID || '');
  if (user?.userCategory || user?.businessType) {
    cookieStorage.setItem(COOKIE_KEYS.USER_CATEGORY, user?.userCategory || '');
    cookieStorage.setItem(COOKIE_KEYS.BUSINESS_TYPE, user?.businessType || '');
  }
  if (user?.userReferalCode) {
    cookieStorage.setItem(COOKIE_KEYS.REFERRAL_CODE, user?.userReferalCode);
  }

  // Agent / employee only: new-user and inactivity flags
  if (user?.userType !== 'CLIENT') {
    if (!user?.lastLoggedInAt) {
      cookieStorage.setItem(COOKIE_KEYS.IS_NEW_USER, 'true');
    }
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    if (new Date(user?.lastLoggedInAt) < threeMonthsAgo) {
      cookieStorage.setItem(COOKIE_KEYS.INACTIVE_USER, 'true');
    }
  }

  cookieStorage.setItem(COOKIE_KEYS.LAST_LOGGED_IN_AT, user?.lastLoggedInAt || '');

  // dashboardConfigSteps: non-sensitive setup-wizard config; kept in localStorage
  // to avoid cookie size pressure from large JSON arrays.
  localStorage.setItem('dashboardConfigSteps', JSON.stringify(user?.dashboardConfigSteps || []));
}
